import type React from "react";
import { forwardRef } from "react";

export type ButtonVariant =
	| "primary"
	| "outlined"
	| "ghost"
	| "tonal"
	| "reset"
	| "link";
export type ButtonSize = "sm" | "lg";

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: ButtonVariant;
	size?: ButtonSize;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	({ variant = "primary", size = "lg", className = "", ...props }, ref) => {
		const baseStyles = [
			"inline-flex",
			"font-rubik",
			"items-center",
			"justify-center",
			"gap-6",
			"font-medium",
			"transition-all",
			"duration-200",
			"ease-in-out",
			"rounded-24",
			"border",
			"focus:outline-none",
			variant !== "link" && "focus:ring-2",
			variant !== "link" && "focus:ring-offset-2",
			"disabled:cursor-not-allowed",
		]
			.filter(Boolean)
			.join(" ");

		const sizeStyles = {
			sm: "px-12 py-6 text-sm min-w-[100px]",
			lg: "px-20 py-10 text-sm min-w-[120px]",
		};

		const variantStyles = {
			primary: {
				base: "bg-primary-500 text-grayscale-white border-primary-500 shadow-sm",
				hover: "hover:bg-primary-600 hover:border-primary-600",
				focus: "focus:ring-primary-500/20",
				active: "active:bg-primary-700 active:border-primary-700",
				disabled:
					"disabled:bg-grayscale-400 disabled:border-grayscale-400 disabled:text-grayscale-white",
			},
			outlined: {
				base: "bg-transparent text-primary-500 border-primary-500 !important border",
				hover: "hover:bg-primary-500 hover:border-primary-500 hover:text-white",
				focus: "focus:ring-primary-500/20",
				active:
					"active:bg-primary-500/10 active:border-primary-500 active:text-primary-500",
				disabled:
					"disabled:bg-transparent disabled:border-grayscale-400 disabled:text-grayscale-400",
			},
			ghost: {
				base: "bg-primary-200/20 text-primary-500 border-transparent",
				hover: "hover:bg-primary-200/30",
				focus: "focus:ring-primary-500/20",
				active: "active:bg-primary-200/40",
				disabled:
					"disabled:bg-grayscale-50 disabled:text-grayscale-400 disabled:border-transparent",
			},
			tonal: {
				base: "bg-grayscale-800 text-grayscale-white border-grayscale-800 shadow-sm",
				hover: "hover:bg-grayscale-700 hover:border-grayscale-700",
				focus: "focus:ring-grayscale-800/20",
				active: "active:bg-grayscale-900 active:border-grayscale-900",
				disabled:
					"disabled:bg-grayscale-400 disabled:border-grayscale-400 disabled:text-grayscale-white",
			},
			reset: {
				base: "bg-grayscale-100 text-grayscale-700 border-grayscale-100 shadow-sm",
				hover: "hover:bg-grayscale-200 hover:border-grayscale-200",
				focus: "focus:ring-grayscale-100/20",
				active: "active:bg-grayscale-300 active:border-grayscale-300",
				disabled:
					"disabled:bg-grayscale-50 disabled:border-grayscale-50 disabled:text-grayscale-400",
			},
			link: {
				base: "bg-transparent text-grayscale-950 border-transparent shadow-none !ring-0 !ring-offset-0 focus-visible:!ring-0 focus-visible:!ring-offset-0",
				hover: "hover:text-grayscale-700 hover:underline",
				focus: "!focus:ring-0 !focus:ring-offset-0 focus:!outline-none",
				active:
					"active:text-grayscale-800 active:!ring-0 active:!ring-offset-0",
				disabled:
					"disabled:bg-transparent disabled:border-transparent disabled:text-grayscale-400",
			},
		};

		const currentVariant = variantStyles[variant];

		const buttonClasses = [
			baseStyles,
			sizeStyles[size],
			currentVariant.base,
			currentVariant.hover,
			currentVariant.focus,
			currentVariant.active,
			currentVariant.disabled,
			className,
		]
			.filter(Boolean)
			.join(" ");

		return <button ref={ref} className={buttonClasses} {...props} />;
	},
);

Button.displayName = "Button";

export default Button;
